import type { WorkbookDefinition } from "@erp/shared-types";
import type { Schema, SheetPlan, WorkbookPlan } from "./planTypes";
import { sheetKind } from "./sheetKinds";
import { tableItems, type WorkbookTableItem } from "./tableReferences";

/**
 * The columns a Run reads of one table: those named, or every column
 * (`"all"`) when what reads it can't be bounded to names.
 */
export type ColumnSet = readonly string[] | "all";

/** What one Worksheet reads of one table it reads. */
export interface ColumnRead {
  tableId: string;
  columns: ColumnSet;
}

/** What finding a Worksheet's column reads needs: its Plan, every table's schema, the tables references name. */
export interface ColumnReadContext {
  /** The Worksheet's Plan; undefined when it has none. */
  plan: SheetPlan | undefined;
  schemas: ReadonlyMap<string, Schema>;
  items: readonly WorkbookTableItem[];
}

/** The union of `reads` of one table: every column when any read is. */
function unionColumns(reads: readonly ColumnSet[]): ColumnSet {
  const named = new Set<string>();
  for (const columns of reads) {
    if (columns === "all") return "all";
    for (const column of columns) named.add(column);
  }
  return [...named];
}

/**
 * The columns a Run of `sheetIds` (every Worksheet when absent) reads of each
 * Source, by Source id, in the Source's column order: what its Worksheets'
 * Formulas, Lookups, filters, sorts and pivot fields name. A Source whose
 * columns the Plan doesn't know, that a freeform sheet reads by position (a
 * reference stored before names), or that is an inline table (held in the
 * Definition, and replaced whole by a Parameter) is read whole (`"all"`). A
 * Source read for no column still reads its first, so its rows are read.
 * Only Sources are pruned: a Worksheet reading a Worksheet reads its Result.
 */
export function usedSourceColumns(
  definition: WorkbookDefinition,
  plan: WorkbookPlan,
  sheetIds?: readonly string[],
): Map<string, ColumnSet> {
  const computed = sheetIds ? new Set(sheetIds) : null;
  const items = tableItems(definition, plan.tables);
  const reads = new Map<string, ColumnSet[]>();
  for (const sheet of definition.sheets) {
    if (computed && !computed.has(sheet.id)) continue;
    const context = {
      plan: plan.sheets.get(sheet.id),
      schemas: plan.tables,
      items,
    };
    for (const read of sheetKind(sheet).columnReads(context))
      reads.set(read.tableId, [
        ...(reads.get(read.tableId) ?? []),
        read.columns,
      ]);
  }
  return new Map(
    definition.sources.map((source): [string, ColumnSet] => {
      const schema = plan.tables.get(source.id);
      const columns = unionColumns(reads.get(source.id) ?? []);
      if (!schema || columns === "all" || source.kind === "input")
        return [source.id, "all"];
      const named = new Set(columns);
      const kept = schema.flatMap(({ name }) =>
        named.has(name) ? [name] : [],
      );
      const first = schema[0]?.name;
      return [source.id, kept.length || first === undefined ? kept : [first]];
    }),
  );
}

/**
 * Whether a Snapshot of a Source that captured `captured` of its columns
 * holds every column `needed` names (every one of `sourceColumns`, the
 * Source's columns when captured, for `"all"`), so a Run can reuse it.
 */
export function capturesColumns(
  captured: readonly string[],
  needed: ColumnSet,
  sourceColumns: readonly string[],
): boolean {
  const held = new Set(captured);
  return (needed === "all" ? sourceColumns : needed).every((column) =>
    held.has(column),
  );
}
